import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../api';
import { Eye, EyeOff } from 'react-feather';
import { FaGoogle, FaApple } from 'react-icons/fa'; // Asegúrate de tener react-icons instalado

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const formDetails = new URLSearchParams();
    formDetails.append('username', formData.username);
    formDetails.append('password', formData.password);

    try {
      const response = await fetch(`${API_URL}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDetails,
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('token', data.access_token);

      const userRes = await fetch(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${data.access_token}` }
      });
      if (!userRes.ok) throw new Error('Failed to fetch user info');
      const userData = await userRes.json();
      
      login(userData);
      
      const redirectPath = localStorage.getItem('redirectAfter');
      if (redirectPath) {
        localStorage.removeItem('redirectAfter');
        if (redirectPath.includes('promoter-link')) {
          localStorage.setItem('redirectAfter', redirectPath);
          navigate(redirectPath);
        } else {
          navigate('/profile');
        }
      } else {
        navigate('/profile');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen w-full flex bg-[#050505] text-white font-sans overflow-hidden">
      
      {/* --- LEFT SIDE: LOGIN FORM --- */}
      <div className="w-full md:w-[50%] lg:w-[45%] flex flex-col justify-center px-8 sm:px-12 md:px-20 lg:px-24 z-10 relative">
        
        {/* Mobile-only background overlay to make text readable if sphere peeks through */}
        <div className="md:hidden mt-16 absolute inset-0 bg-black/90 z-[-1]" />

        <div className="max-w-md w-full mx-auto">
          <div className="mb-10">
            <h1 className="text-4xl md:text-5xl font-medium tracking-tight mb-3 text-white">Bienvenido</h1>
            <p className="text-gray-400 text-base font-light">Ingresa tus credenciales para acceder al panel.</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Social Login Buttons (Visual Only) */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <button type="button" className="flex items-center justify-center gap-3 py-3 rounded-xl border border-white/10 hover:bg-white/5 transition-colors text-sm font-medium text-white/80">
                <FaGoogle className="text-lg" /> Google
              </button>
              <button type="button" className="flex items-center justify-center gap-3 py-3 rounded-xl border border-white/10 hover:bg-white/5 transition-colors text-sm font-medium text-white/80">
                <FaApple className="text-xl" /> Apple
              </button>
            </div>

            <div className="relative flex py-2 items-center">
              <div className="flex-grow border-t border-white/10"></div>
              <span className="flex-shrink mx-4 text-gray-500 text-xs uppercase tracking-wider">O continúa con email</span>
              <div className="flex-grow border-t border-white/10"></div>
            </div>

            {/* Inputs */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                  Usuario / DNI
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="ej. usuario123"
                  className="w-full px-5 py-4 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full px-5 py-4 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Checkbox & Forgot Password */}
            <div className="flex items-center justify-between pt-2">
              <Link to="/forgot-password" className="text-sm text-white/60 hover:text-white transition-colors hover:underline decoration-white/30 underline-offset-4">
                ¿Olvidaste la clave?
              </Link>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center animate-pulse">
                {error}
              </div>
            )}

            {/* Main Action Button - White Pill Style */}
            <button
              type="submit"
              className="w-full py-4 mt-4 bg-white text-black font-semibold rounded-full hover:scale-[1.01] active:scale-[0.98] transition-all shadow-[0_0_20px_-5px_rgba(255,255,255,0.2)]"
            >
              Iniciar Sesión
            </button>

            <div className="text-center mt-8">
              <span className="text-gray-500 text-sm">¿Aún no tienes cuenta? </span>
              <Link to="/register" className="text-white hover:underline decoration-white/50 underline-offset-4 font-medium text-sm ml-1">
                Regístrate gratis
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* --- RIGHT SIDE: ANIMATED SPHERE (OPTIMIZED) --- */}
      {/* Usamos bg-black puro para que se fusione con el fondo del GIF sin necesidad de blend-modes */}
      <div className="hidden md:block w-[50%] lg:w-[55%] relative bg-black overflow-hidden">
        
        {/* Eliminé el 'glow' de fondo porque el blur también consume muchos recursos si hay movimiento encima */}
        
        {/* The Sphere Image/Gif */}
        <div className="absolute inset-0 flex items-center justify-center">
             <img 
              src="/gif/spherefig.gif" 
              alt="Artistic Sphere" 
              // Quitamos mix-blend-screen y filters. 
              // Usamos 'will-change-transform' para forzar aceleración de GPU si es necesario.
              className="w-full h-full object-cover will-change-transform" 
            />
        </div>
        
        {/* Texto sobrepuesto (con z-index para asegurar que flote encima) */}
        <div className="absolute bottom-12 left-12 max-w-sm z-10 pointer-events-none">
            <p className="text-white/60 text-sm font-serif italic">"Access the night."</p>
        </div>
      </div>

    </div> // Cierre del div principal
  );
}